/**
 * App-side wiring for `@heidi/evidence/data` (the `evidence/source` carve).
 *
 * SIX things cross, and five of them arrived together with the chip-query
 * carve because that hook is a REQUEST BUILDER over SSE rather than an axios
 * call. Every other carved read in this family goes out through the scribe
 * instance, whose interceptors attach the bearer, the temp-user id and the
 * client-identity headers; `useTranscriptChipsQuery` streams through
 * `@fortaine/fetch-event-source`, gets none of that, and assembles the request
 * by hand out of four app facts the package cannot resolve.
 *
 * - `queryClient` — the app's `reactQueryClient` singleton, and the original
 *   input. Two exports of the carved config read are MODULE-SCOPE cache
 *   commands rather than hooks: `getCachedEvidenceConfig` reads the cached
 *   config without subscribing, and `invalidateEvidenceConfig` evicts it. The
 *   second is called from outside React entirely —
 *   `src/hooks/mutation/useMutateTeamShortcut.ts` fires it from a mutation
 *   callback, and that call stays app-side permanently because `workspace` is
 *   FOUNDATIONAL and can take no product-domain value import. With no
 *   `useQueryClient()` in reach, the singleton is what crosses — the severance
 *   cohort S3 mechanism, the same one `coding-data-wiring` and
 *   `session-data-wiring` use. It is the very instance the app seeds
 *   `PersistQueryClientProvider` with, so cache identity and invalidation timing
 *   are unchanged.
 *
 *   Safe to import statically: `@/utils/reactQueryClient` is already in `_app`'s
 *   module cone through the provider itself, so this adds nothing to the bundle
 *   and pulls no store-reaching module early.
 *
 * - `loadAuthToken` — A LOADER, AND THE INDIRECTION IS LOAD-BEARING. Do not
 *   "simplify" it to a static `import { getAuthTokenForHttpClient }`. This
 *   module is imported for effect by `_app`, so a static import would evaluate
 *   that helper's cone at `_app` module scope, and the cone reaches `@/store` —
 *   an async module under rspack/webpack. Pulling it that early is exactly what
 *   produced `ReferenceError: Cannot access 'v' before initialization` during
 *   "Collecting page data" on the integrations carve, on pages with
 *   no connection to that domain; `src/utils/dictation-data-wiring.ts` records
 *   the same trap for the same reason. Nothing local catches this class — tsc,
 *   jest and lint are all blind to module-initialisation order in a bundled
 *   chunk — so the SHAPE is the guard and only a production build catches a
 *   regression. Deferring also restores the pre-carve timing: the hook imported
 *   the helper directly, so its cone was evaluated only when a chip surface
 *   mounted.
 *
 * - `getXClientHeaders`, `getTempUserId`, `getDomains`, `getWorkspaceIdHeader`
 *   — plain getters, all four safe to import statically (client-identity headers
 *   read a build constant, the temp-user id reads `localStorage`, `getDomains`
 *   is already in `_app`'s cone through `scribeHTTP`, and `@heidi/workspace/data`
 *   is pulled by `workspace-data-wiring` 35 lines above this import). They cross
 *   as GETTERS rather than values because each is read at request time: the
 *   temp-user id is written during the anonymous flow, the active workspace
 *   changes with the workspace switcher, and the region behind `getDomains` can
 *   change within a session, so a value captured here would be wrong for the
 *   session it is supposed to describe.
 *
 *   `getWorkspaceIdHeader` CROSSES THE SEAM RATHER THAN BEING IMPORTED
 *   PACKAGE-SIDE, and the distinction between the two workspace rules is the
 *   reason. §5.3 makes workspace FOUNDATIONAL, so evidence may depend on it
 *   without a `DOMAIN_EDGES` entry; §5.2 Aperture 1 law 4 separately fixes WHICH
 *   DOOR a `/data` zone may use — `/contracts` only — and that one is enforced
 *   by `@typescript-eslint/no-restricted-imports`. The helper is a runtime jotai
 *   read, so it cannot live in an inert `/contracts` door. Same shape as
 *   `integrations-data-wiring`'s `useWorkspaceId`.
 *
 * FOUR MORE ARRIVED WITH THE COLLECTIONS CARVE, and they are a different KIND
 * of input again: not transport facts but the terms every collections and
 * source-list READ composed into its `enabled`. Those lived in app-side
 * delegates under the app's old library folder until it was deleted
 * (no shim, ruling Di 2026-10-08), so with no old path left to compute them
 * they cross here.
 *
 * - `isEvidenceLibraryEnabled` / `isUnifiedCollectionsEnabled` — THUNKS over
 *   the legacy getters, never resolved booleans: a boolean captured here
 *   freezes the flip for the life of the tab, and each is read inside the hook
 *   body on every render exactly where the deleted delegates read it. The
 *   first is a PostHog read and returns `undefined` while that client loads —
 *   a tri-state react-query reads as ENABLED, which is live behaviour on every
 *   cold load and is why nothing here coerces it.
 *
 * - `useSourceCatalogue` — the app's `useQuerySources` DELEGATE, with its
 *   on-prem surface gate already applied. It crosses rather than the package
 *   calling the door's own `useQuerySources`, because
 *   `src/utils/backendSurfaces.guard.test.ts` holds that delegate to being the
 *   hook's only importer outside the package: that chokepoint is what keeps
 *   the gate closable, and a second entry point would silently reopen the
 *   on-prem 404. It is the ONE binding here that is not already in `_app`'s
 *   cone, and it is a 32-line module over `@/utils/backendSurfaces` — a build
 *   profile read — plus the door, so it pulls no store.
 *
 * WHAT IS DELIBERATELY NOT HERE, so the omission reads as a measurement:
 * `useAuthToken`, the app's REACTIVE token hook. No carved module needs one —
 * all five carved reads gated on `!!token && <app fact>` composed inline, and
 * because BOTH terms are app facts they collapse into the single
 * caller-computed boolean each delegate now hands in. The chip query needs the
 * token VALUE at request time, not a subscription, which is what
 * `loadAuthToken` gives it.
 *
 * FOUR MORE ARRIVED WITH THE USER-DOCUMENTS CARVE, and three of them are a
 * different KIND again: an EFFECT the package fires rather than a fact it
 * reads.
 *
 * - `isEvidenceUserDocsEnabled` — a fourth flag THUNK, on the rule above. A
 *   PostHog read, so it is `undefined` while that client loads and nothing
 *   here coerces it: that tri-state is what `useUserDocuments` has gated on
 *   since before the carve.
 *
 * - `invalidateGamifyTrialExtension` — the commerce invalidation plus the
 *   billing-info eviction, fired after a document upload settles. It crosses
 *   rather than the package calling `@heidi/commerce/data` itself, because
 *   §5.2 Aperture 1 law 4 lets a zone reach a foreign domain through
 *   `/contracts` only and an invalidator is not inert. COMPOSED HERE FROM THE
 *   TWO DOOR COMMANDS rather than taken from the `src/hooks/query` delegate
 *   that spells the same pair: the delegate is a `commerce/trials` family
 *   subject whose legacy-importer list is shrink-only, and this module
 *   binding it would mint a row there while the two library hooks that used
 *   to hold one are leaving. Both commands are the same functions the
 *   delegate calls, in the same order, awaited together.
 *
 * - `trackLibraryEvent` — the six library events' EMIT. The vocabulary is
 *   evidence's (`LibraryAnalyticsEvent` in its `/contracts`), the enum and
 *   the destination options are the app's, so the mapping lives here: one
 *   `track()` call per union member, with `{ amplitude: false }` kept on
 *   exactly the three that carry it today. `@/lib/analytics/track` reaches no
 *   store.
 *
 * - `useTrackChatError` — a HOOK, and the second on this seam, for the reason
 *   `useIsAuthenticated` is one: the app's `trackChatError` closes over
 *   `useEvidenceAnalytics`' per-render base props (workspace, region, session
 *   stage, source-selection mode), so a function captured at this call would
 *   stamp every delete failure with the boot context. `useEvidenceAnalytics`
 *   reaches `@/store` through `useAuthToken`, but `_app` already pulls that
 *   cone, so naming it here adds nothing that was not evaluated at `_app`
 *   scope — the same test `loadAuthToken` fails and `useIsAuthenticated`
 *   passes.
 *
 * TWO MORE ARRIVED WITH THE DOCUMENT-PREVIEW CARVE.
 *
 * - `isBulkUploadEnabled` — a fifth flag THUNK, on the rule above.
 *
 * - `reactPdf` — react-pdf's `Document` and `Page`, already wrapped in the
 *   app's `ssr: false` dynamic loader. THE WORKER PATH IS WHY: the loader sets
 *   `pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'`, an absolute
 *   path into the app's `public/` that the package neither owns nor serves, so
 *   a package-side `react-pdf` import would be a second pdf.js instance with no
 *   worker — a first-render failure rather than a build one. Both bindings are
 *   `next/dynamic` wrappers, so naming them here evaluates the loader module
 *   and nothing of react-pdf itself.
 *
 * TWO MORE ARRIVED WITH THE SOURCE-LIST SURFACES.
 *
 * - `getRegion` — a THUNK, and the reason is freshness rather than policy:
 *   the helper resolves the region from the Kinde cache, the EHR-launch atom,
 *   the widget atom and the host name, and an EHR launch can hand one in
 *   mid-session. A value read here would pin the first answer for the tab.
 *
 * - `useUpdateEvidenceSettings` — a HOOK, and the third on this seam. The
 *   upload-risk opt-in keeps its body package-side because the READ is
 *   `@heidi/workspace/data`'s `useQueryUser`, which a `/ui` zone may import;
 *   the WRITE is the app's `useMutateUpdateUser`, whose REACTIONS are what
 *   make it correct and which the door's hook of the same name does not carry
 *   (ruling 65). It is bound through `useUpdateUser`, the workspace door's
 *   injected reader for exactly that delegate — `workspace-data-wiring` binds
 *   `useUpdateUser: useMutateUpdateUser`, so this is the app hook, reached by
 *   the one published name for it rather than by a second direct importer of
 *   a `workspace/identity-projection` subject whose list is shrink-only.
 *   `{ enableInvalidation: false }` is passed through, so the acceptance
 *   write still skips the user-query invalidation it always skipped.
 *
 * ONE MORE ARRIVED WITH THE RUNTIME CAPABILITIES (MOD-1726).
 *
 * - `capabilitiesReportedByBackend` — a BUILD FACT, so a plain boolean: whether
 *   this build asks `GET /chat/capabilities` at all. Cloud does not (no
 *   request, unrestricted); an appliance does, and the answer is runtime (F9).
 *
 * Imported at module scope in `_app.tsx`, after `workspace-data-wiring`.
 */
import { areEvidenceCapabilitiesReportedByBackend } from '@/atoms/evidenceCapabilitiesAtoms';
import {
  IS_BULK_UPLOAD_ENABLED,
  IS_EVIDENCE_LIBRARY_ENABLED,
  IS_EVIDENCE_USER_DOCS_ENABLED,
  IS_UNIFIED_COLLECTIONS_ENABLED,
} from '@/features/flags';
import { getDomains } from '@/helpers/getDomains';
import { getRegion } from '@/helpers/getRegion';
import { useQuerySources } from '@/hooks/query/useQuerySources';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { track } from '@/lib/analytics/track';
import { ReactPdfDocument, ReactPdfPage } from '@/lib/pdf/react-pdf-loader';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { getXClientHeaders } from '@/utils/http/clientIdentityHeaders';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { getTempUserId } from '@/utils/tempUser';
import {
  invalidateCurrentUserBillingInfo,
  invalidateGamifyTrialExtension,
} from '@heidi/commerce/data';
import type { LibraryAnalyticsEvent } from '@heidi/evidence/contracts';
import { configureEvidenceData } from '@heidi/evidence/data';
import { getWorkspaceIdHeader, useUpdateUser } from '@heidi/workspace/data';

/**
 * The library-event mapping, exhaustive by construction.
 *
 * `{ amplitude: false }` is carried by exactly three of the six, which is what
 * they carried at the call sites before the carve. Dropping it from any of
 * them resends that event to Amplitude with no call site saying so.
 */
const emitLibraryEvent = (event: LibraryAnalyticsEvent): void => {
  switch (event.name) {
    case 'library_document_uploaded':
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.LibraryDocumentUploaded,
        props: event.props,
      });
      return;
    case 'library_collection_created':
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.LibraryCollectionCreated,
        props: event.props,
      });
      return;
    case 'library_upload_nudge_clicked':
      track({
        event_name: TrackingEventName.LibraryUploadNudgeClicked,
        props: event.props,
      });
      return;
    case 'library_document_added_to_collection':
      track({
        event_name: TrackingEventName.LibraryDocumentAddedToCollection,
        props: event.props,
      });
      return;
    case 'library_link_clicked':
      track({
        event_name: TrackingEventName.LibraryLinkClicked,
        props: event.props,
      });
      return;
    case 'library_document_scope_changed':
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.LibraryDocumentScopeChanged,
        props: event.props,
      });
      return;
  }
};

configureEvidenceData({
  queryClient: reactQueryClient,
  capabilitiesReportedByBackend: areEvidenceCapabilitiesReportedByBackend,
  loadAuthToken: () =>
    import('@/utils/http/getAuthTokenForHttpClient').then(
      (m) => m.getAuthTokenForHttpClient
    ),
  getXClientHeaders,
  getTempUserId,
  getDomains: () => getDomains(),
  getWorkspaceIdHeader,
  isEvidenceLibraryEnabled: () => IS_EVIDENCE_LIBRARY_ENABLED(),
  isUnifiedCollectionsEnabled: () => IS_UNIFIED_COLLECTIONS_ENABLED(),
  useSourceCatalogue: () => {
    const { data, isLoading } = useQuerySources();
    return { data, isLoading };
  },
  isEvidenceUserDocsEnabled: () => IS_EVIDENCE_USER_DOCS_ENABLED(),
  invalidateGamifyTrialExtension: async () => {
    await Promise.all([
      invalidateGamifyTrialExtension(),
      invalidateCurrentUserBillingInfo(),
    ]);
  },
  trackLibraryEvent: emitLibraryEvent,
  useTrackChatError: () => useEvidenceAnalytics().trackChatError,
  isBulkUploadEnabled: () => IS_BULK_UPLOAD_ENABLED(),
  reactPdf: { Document: ReactPdfDocument, Page: ReactPdfPage },
  getRegion: () => getRegion(),
  useUpdateEvidenceSettings: () => useUpdateUser({ enableInvalidation: false }).mutate,
});
